import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import RocketLaunchRounded from "@mui/icons-material/RocketLaunchRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import ShieldRounded from "@mui/icons-material/ShieldRounded";
import BoltRounded from "@mui/icons-material/BoltRounded";
import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { getAllImagesAndUrl } from "../../BookingDashboard/apis/BrandingModalApi";
import useMigrateToNewUi from "../../../../hooks/useMigrateToNewUi";

const NEW_UI_URL = "https://pms.stayflexi.com/";

// The modal always targets one property: the one in the URL, else the
// last-used one, else the first in the group; its session is handed over
const resolveHotel = (hotelId, hotels, selectedHotel) => {
  const list = Array.isArray(hotels) ? hotels : [];
  if (hotelId) {
    const inUrl =
      list.find((h) => String(h.hotelId) === String(hotelId)) ||
      (String(selectedHotel?.hotelId) === String(hotelId) ? selectedHotel : null);
    if (inUrl) return inUrl;
  }
  return selectedHotel || list[0];
};

const BRAND_GRADIENT = "linear-gradient(-150deg, #36454F, #000000)";

const BENEFITS = [
  { Icon: ShieldRounded, title: "More secure", text: "Latest security standards" },
  { Icon: BoltRounded, title: "Faster", text: "Quicker pages and actions" },
  { Icon: AutoAwesomeRounded, title: "AI-powered", text: "Smarter tools built in" },
];

const NewUiMigrationModal = () => {
  const { t } = useTranslation();
  const { pathname, search, state } = useLocation();
  // Group dashboard switches sections via router state, not the path
  const selectedSection = state?.selectedSection;
  const hotels = useSelector((s) => s.auth?.hotels);
  const selectedHotel = useSelector((s) => s.auth?.selectedHotel);
  const hotelId = new URLSearchParams(search).get("hotelId");
  const targetHotel = useMemo(
    () => resolveHotel(hotelId, hotels, selectedHotel),
    [hotelId, hotels, selectedHotel]
  );
  const [isWhiteLabel, setIsWhiteLabel] = useState(true);
  const [open, setOpen] = useState(true);
  const { directToNewUi } = useMigrateToNewUi();

  // Hide only for domains whose branding is ENABLED; a row with status
  // DISABLED (e.g. localhost) or a failed lookup still shows the notice
  useEffect(() => {
    getAllImagesAndUrl()
      .then((brandingData) => {
        setIsWhiteLabel(brandingData?.status === "ENABLED");
      })
      .catch(() => setIsWhiteLabel(false));
  }, []);

  // Re-open on every page switch; a reload remounts with open = true
  useEffect(() => {
    setOpen(true);
  }, [pathname, selectedSection]);

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return;
    setOpen(false);
  };

  // Hand the session over like the header's New UI button, so the property is
  // signed in on arrival
  const goToNewUi = () => {
    if (targetHotel?.hotelId) {
      directToNewUi(targetHotel);
      return;
    }
    window.location.href = NEW_UI_URL;
  };

  if (isWhiteLabel) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="new-ui-migration-dialog-title"
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(15, 23, 42, 0.6)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: BRAND_GRADIENT,
          color: "#fff",
          px: { xs: 3, sm: 4 },
          pt: 4,
          pb: 3.5,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 220,
            height: 220,
            borderRadius: "50%",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            top: -90,
            right: -60,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 140,
            height: 140,
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            bottom: -70,
            right: 70,
          }}
        />
        <IconButton
          aria-label={t("Close")}
          onClick={handleClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255, 255, 255, 0.8)",
            "&:hover": {
              color: "#fff",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
            },
          }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ position: "relative", pr: 3 }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              border: "1px solid rgba(255, 255, 255, 0.18)",
            }}
          >
            <RocketLaunchRounded sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Chip
              label={t("Important update")}
              size="small"
              sx={{
                height: 22,
                mb: 0.75,
                fontSize: "0.7rem",
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: "#FDE68A",
                backgroundColor: "rgba(253, 230, 138, 0.14)",
              }}
            />
            <Typography
              id="new-ui-migration-dialog-title"
              component="h2"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.4rem" },
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              {t("We are moving to the new Stayflexi UI")}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={{ px: { xs: 3, sm: 4 }, pt: 3, pb: 1 }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            p: 1.75,
            borderRadius: "12px",
            backgroundColor: "#FFF7ED",
            border: "1px solid #FED7AA",
          }}
        >
          <WarningAmberRounded sx={{ color: "#C2410C" }} />
          <Typography
            sx={{ flex: 1, fontSize: "0.9rem", fontWeight: 500, color: "#7C2D12" }}
          >
            {t("This UI will stop working soon.")}
          </Typography>
        </Stack>

        <Typography
          sx={{ mt: 2.5, fontSize: "0.95rem", lineHeight: 1.6, color: "text.secondary" }}
        >
          {t(
            "Due to security reasons and platform upgrades, we are shifting to the new Stayflexi UI. Please migrate to the new UI to continue using Stayflexi without interruption."
          )}
        </Typography>

        <Box
          sx={{
            mt: 2.5,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 1.5,
          }}
        >
          {BENEFITS.map(({ Icon, title, text }) => (
            <Box
              key={title}
              sx={{
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "grey.200",
                backgroundColor: "grey.50",
                display: "flex",
                flexDirection: { xs: "row", sm: "column" },
                alignItems: { xs: "center", sm: "flex-start" },
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: BRAND_GRADIENT,
                  color: "#fff",
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                  {t(title)}
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                  {t(text)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Actions */}
      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        spacing={1.5}
        justifyContent="flex-end"
        sx={{ px: { xs: 3, sm: 4 }, pt: 2.5, pb: 3 }}
      >
        <Button
          onClick={handleClose}
          sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
        >
          {t("Remind me later")}
        </Button>
        <Button
          variant="custom-button"
          size="large"
          endIcon={<ArrowForwardRounded />}
          onClick={goToNewUi}
          sx={{
            px: 3,
            borderRadius: "10px",
            fontWeight: 600,
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.25)",
          }}
        >
          {t("Go to New UI")}
        </Button>
      </Stack>
    </Dialog>
  );
};

export default NewUiMigrationModal;
